import { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import {
  Drawer,
  Form,
  Input,
  InputNumber,
  Switch,
  DatePicker,
  Button,
  Space,
  Typography,
  Alert,
  message,
} from 'antd';
import { DollarCircleOutlined, CheckOutlined } from '@ant-design/icons';
import dayjs, { type Dayjs } from 'dayjs';
import { useStore } from '@/stores';
import type { AiModelPricing } from '@/api/modelPricing';
import { colors } from '@/theme/tokens';
import { trackEvent } from '@/lib/telemetry';

const { Text } = Typography;

interface ModelPricingEditorDrawerProps {
  open: boolean;
  // null = create mode; otherwise edit the existing row
  editing: AiModelPricing | null;
  // When creating a new rate for an existing model card, seed model + provider
  seed?: { modelName: string; provider: string } | null;
  onClose: () => void;
}

interface FormShape {
  modelName: string;
  displayName?: string;
  provider: string;
  inputPer1MTokens: number;
  outputPer1MTokens: number;
  cachedInputPer1MTokens?: number | null;
  reasoningOutputPer1MTokens?: number | null;
  effectiveDate: Dayjs;
  expirationDate?: Dayjs | null;
  isActive: boolean;
  notes?: string;
}

// Drawer for adding or editing one AiModelPricing row. Authoring conventions
// are surfaced inline so admins don't accidentally rewrite an audit row that
// PromptLog already references for historical cost calculations.

export const ModelPricingEditorDrawer = observer(function ModelPricingEditorDrawer({
  open,
  editing,
  seed,
  onClose,
}: ModelPricingEditorDrawerProps) {
  const { modelPricingStore: store, themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';
  const [form] = Form.useForm<FormShape>();
  const [submitting, setSubmitting] = useState(false);

  const isEdit = editing != null;

  useEffect(() => {
    if (!open) return;
    if (editing) {
      form.setFieldsValue({
        modelName: editing.modelName,
        displayName: editing.displayName,
        provider: editing.provider ?? 'OpenAI',
        inputPer1MTokens: editing.inputPer1MTokens,
        outputPer1MTokens: editing.outputPer1MTokens,
        cachedInputPer1MTokens: editing.cachedInputPer1MTokens ?? null,
        reasoningOutputPer1MTokens: editing.reasoningOutputPer1MTokens ?? null,
        effectiveDate: dayjs(editing.effectiveDate),
        expirationDate: editing.expirationDate ? dayjs(editing.expirationDate) : null,
        isActive: editing.isActive,
        notes: editing.notes,
      });
    } else {
      form.setFieldsValue({
        modelName: seed?.modelName ?? '',
        displayName: undefined,
        provider: seed?.provider ?? 'OpenAI',
        inputPer1MTokens: 0,
        outputPer1MTokens: 0,
        cachedInputPer1MTokens: null,
        reasoningOutputPer1MTokens: null,
        effectiveDate: dayjs(),
        expirationDate: null,
        isActive: true,
        notes: undefined,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing?.id, seed?.modelName]);

  async function handleSubmit(): Promise<void> {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      const payload: Partial<AiModelPricing> = {
        ...(isEdit && editing ? { id: editing.id } : {}),
        modelName: values.modelName.trim(),
        displayName: values.displayName?.trim() || undefined,
        provider: values.provider.trim() || 'OpenAI',
        inputPer1MTokens: Number(values.inputPer1MTokens),
        outputPer1MTokens: Number(values.outputPer1MTokens),
        cachedInputPer1MTokens:
          values.cachedInputPer1MTokens == null ? null : Number(values.cachedInputPer1MTokens),
        reasoningOutputPer1MTokens:
          values.reasoningOutputPer1MTokens == null
            ? null
            : Number(values.reasoningOutputPer1MTokens),
        effectiveDate: values.effectiveDate.toISOString(),
        expirationDate: values.expirationDate ? values.expirationDate.toISOString() : null,
        isActive: values.isActive,
        notes: values.notes?.trim() || undefined,
      };
      if (isEdit) {
        await store.update(payload);
        message.success('Pricing row updated');
        trackEvent('Pricing.Updated', { id: editing!.id });
      } else {
        await store.create(payload);
        message.success('Pricing row added');
        trackEvent('Pricing.Added', { modelName: payload.modelName });
      }
      form.resetFields();
      onClose();
    } catch (err: unknown) {
      const isFormError = err && typeof err === 'object' && 'errorFields' in err;
      if (!isFormError) {
        message.error(err instanceof Error ? err.message : 'Failed to save');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Drawer
      open={open}
      onClose={() => {
        form.resetFields();
        onClose();
      }}
      width={580}
      title={
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <DollarCircleOutlined /> {isEdit ? 'Edit pricing row' : seed ? `Add new rate for ${seed.modelName}` : 'New pricing row'}
        </span>
      }
      destroyOnClose
      styles={{ body: { padding: '20px 22px' } }}
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Space>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              type="primary"
              loading={submitting}
              onClick={handleSubmit}
              icon={<CheckOutlined />}
            >
              {isEdit ? 'Save changes' : 'Add rate'}
            </Button>
          </Space>
        </div>
      }
    >
      {isEdit && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Editing an existing rate"
          description={
            <span style={{ fontSize: 12.5 }}>
              Use this for typo / display-name / notes fixes only. <b>Don't change rate values
              on a row that has already produced PromptLog.EstimatedCostUsd entries</b> —
              historical cost reports will distort. Instead, close this and click{' '}
              <i>Add new rate</i> on the model card.
            </span>
          }
        />
      )}

      <Form form={form} layout="vertical" preserve={false} requiredMark="optional">
        <SectionLabel isDark={isDark}>Identity</SectionLabel>

        <Form.Item
          name="modelName"
          label="Model name"
          extra="Must exactly match PromptLog.ModelName. Examples: gpt-4o, o3-mini, gpt-4.1"
          rules={[{ required: true, message: 'Model name is required' }]}
        >
          <Input
            placeholder="gpt-4o"
            disabled={!!seed}
            style={{ fontFamily: "'JetBrains Mono', monospace" }}
          />
        </Form.Item>

        <Form.Item
          name="provider"
          label="Provider"
          extra='Distinguishes Azure OpenAI from direct OpenAI when rates differ. Default "OpenAI".'
          rules={[{ required: true }]}
        >
          <Input placeholder="OpenAI" disabled={!!seed} />
        </Form.Item>

        <Form.Item
          name="displayName"
          label="Display name (optional)"
          extra="Human-friendly label for admin UI. Not used in code."
        >
          <Input placeholder='e.g. "GPT-4o (2026-04)"' />
        </Form.Item>

        <SectionLabel isDark={isDark}>Rates · USD per 1,000,000 tokens</SectionLabel>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Form.Item
            name="inputPer1MTokens"
            label="Input"
            rules={[{ required: true, type: 'number', min: 0 }]}
          >
            <InputNumber
              prefix="$"
              step={0.0001}
              min={0}
              style={{ width: '100%' }}
              placeholder="2.5000"
            />
          </Form.Item>
          <Form.Item
            name="outputPer1MTokens"
            label="Output"
            rules={[{ required: true, type: 'number', min: 0 }]}
          >
            <InputNumber
              prefix="$"
              step={0.0001}
              min={0}
              style={{ width: '100%' }}
              placeholder="10.0000"
            />
          </Form.Item>
          <Form.Item
            name="cachedInputPer1MTokens"
            label="Cached input (optional)"
            extra="OpenAI typically gives 50% off cached tokens. Leave blank if no caching discount."
          >
            <InputNumber
              prefix="$"
              step={0.0001}
              min={0}
              style={{ width: '100%' }}
              placeholder="1.2500"
            />
          </Form.Item>
          <Form.Item
            name="reasoningOutputPer1MTokens"
            label="Reasoning output (optional)"
            extra="For reasoning models (o1, o3-mini). Blank for chat models like gpt-4o."
          >
            <InputNumber
              prefix="$"
              step={0.0001}
              min={0}
              style={{ width: '100%' }}
              placeholder="60.0000"
            />
          </Form.Item>
        </div>

        <SectionLabel isDark={isDark}>Validity window</SectionLabel>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Form.Item
            name="effectiveDate"
            label="Effective from"
            extra="When this rate starts applying."
            rules={[{ required: true }]}
          >
            <DatePicker style={{ width: '100%' }} showTime />
          </Form.Item>
          <Form.Item
            name="expirationDate"
            label="Expires (optional)"
            extra="Leave blank if currently active. Set when introducing a successor rate."
          >
            <DatePicker style={{ width: '100%' }} showTime allowClear />
          </Form.Item>
        </div>

        <SectionLabel isDark={isDark}>Status &amp; notes</SectionLabel>

        <Form.Item label="Status" name="isActive" valuePropName="checked">
          <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
        </Form.Item>

        <Form.Item
          name="notes"
          label="Notes"
          extra='Free-text. e.g. "Rate from OpenAI pricing page 2026-04-01".'
        >
          <Input.TextArea rows={3} maxLength={500} showCount />
        </Form.Item>

        <Text type="secondary" style={{ fontSize: 11.5, display: 'block', marginTop: 8 }}>
          Reminder: cost calculation reads the most recent active row where{' '}
          <code>EffectiveDate ≤ now &lt; ExpirationDate</code>.
        </Text>
      </Form>
    </Drawer>
  );
});

function SectionLabel({ children, isDark }: { children: React.ReactNode; isDark: boolean }) {
  return (
    <div
      style={{
        fontSize: 11,
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        color: isDark ? colors.slate[400] : colors.slate[500],
        marginBottom: 12,
        marginTop: 6,
      }}
    >
      {children}
    </div>
  );
}
